import { v4 as uuid } from 'uuid';
import COOKIES from '~/constants/cookies';
import Cookies from '~/utilities/BrowserCookies';

export const SEARCH_SESSION_ID_ROTATED_EVENT =
  'merch:search-session-id-rotated';

let hasSeededFromServer = false;

export function getSearchSessionIdCookie(): string | undefined {
  return Cookies.get(COOKIES.searchSessionId);
}

export function setSearchSessionIdCookie(searchSessionId: string): void {
  if (typeof window === 'undefined') return;

  Cookies.set(COOKIES.searchSessionId, searchSessionId, {
    path: '/',
    sameSite: 'lax',
    secure: window.location.protocol === 'https:'
  });
}

// Safe on any page: the SRP provider, when mounted, follows the cookie through the event.
export function rotateSearchSessionId(): string {
  const searchSessionId = uuid();

  setSearchSessionIdCookie(searchSessionId);
  window.dispatchEvent(
    new CustomEvent(SEARCH_SESSION_ID_ROTATED_EVENT, {
      detail: searchSessionId
    })
  );

  return searchSessionId;
}

export function getNavigationType(): string | undefined {
  const [entry] = performance.getEntriesByType?.('navigation') ?? [];

  return entry && 'type' in entry && typeof entry.type === 'string'
    ? entry.type
    : undefined;
}

// Back/forward can replay cached HTML and later in-app remounts can replay a stale
// payload, so the cookie wins there. Any other document load takes the id the server
// chose, which is new on a reload because a reload never armed the resume marker.
export function resolveInitialSearchSessionId(
  serverSearchSessionId?: string
): string {
  if (typeof window === 'undefined') {
    return serverSearchSessionId ?? uuid();
  }

  const cookieValue = getSearchSessionIdCookie();
  const navigationType = getNavigationType();

  const shouldServerWin =
    !hasSeededFromServer && navigationType !== 'back_forward';
  const [preferred, fallback] = shouldServerWin
    ? [serverSearchSessionId, cookieValue]
    : [cookieValue, serverSearchSessionId];
  const searchSessionId = preferred ?? fallback ?? uuid();

  return searchSessionId;
}

export type SearchSessionBreak = 'back-lost' | 'reload-reused';

// Only on the first load of a document: a reload that kept the id means the resume marker
// reached the server, and a back/forward with no cookie means the session was lost.
export function detectSearchSessionBreak(
  serverSearchSessionId?: string
): SearchSessionBreak | undefined {
  if (typeof window === 'undefined' || hasSeededFromServer) return undefined;

  const previousSearchSessionId = getSearchSessionIdCookie();
  const navigationType = getNavigationType();

  if (navigationType === 'back_forward' && !previousSearchSessionId) {
    return 'back-lost';
  }

  return navigationType === 'reload' &&
    !!serverSearchSessionId &&
    serverSearchSessionId === previousSearchSessionId
    ? 'reload-reused'
    : undefined;
}

export function isFirstSearchSessionLoad(): boolean {
  return !hasSeededFromServer;
}

export function markSearchSessionIdSeeded(): void {
  hasSeededFromServer = true;
}

export function resetSearchSessionIdSeedingForTesting(): void {
  hasSeededFromServer = false;
}
